import React, { useState, useCallback } from "react"
import {
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Col,
  Label,
  Input,
  FormFeedback,
  Button,
  Form,
} from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import { toast } from "react-toastify"
import { useEditShowroom } from "hooks/showroomHook"
import ButtonTooltip from "components/Common/ButtonTooltip"

export default function ShowroomContactUpdate({ showroom = null }) {
  const [modal, setModal] = useState(false)
  const toggle = () => setModal(!modal)

  const { mutateAsync: editShowroom, isPending: showroomEditLoading } =
    useEditShowroom()

  // Validation schema
  const validationSchema = Yup.object({
    mobile: Yup.string().required("Mobile is required"),
    alt_mobile: Yup.string().nullable(),
    email: Yup.string().email("Invalid email").nullable(),
    website: Yup.string().url("Invalid URL").nullable(),
    stock_url: Yup.string().url("Invalid stock URL").nullable(),
    google_map: Yup.string().nullable(),
  })

  // Submit handler
  const onSubmit = useCallback(
    async values => {
      try {
        const res = await editShowroom({
          id: showroom?.id,
          ...values,
          stock_url: values.stock_url || null,
        })
        toggle()
        toast.success(res?.message ?? "Contact info updated successfully.")
      } catch (error) {
        console.error("Failed to update contact info:", error)
        toast.error("Failed to update contact info.")
      }
    },
    [editShowroom, showroom]
  )

  // Formik setup
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      mobile: showroom?.mobile || "",
      alt_mobile: showroom?.alt_mobile || "",
      email: showroom?.email || "",
      website: showroom?.website || "",
      stock_url: showroom?.stock_url || "",
      google_map: showroom?.google_map || "",
    },
    validationSchema,
    onSubmit,
  })

  return (
    <>
      {/* ✅ Edit button */}
      <div className="d-flex justify-content-end">
        <ButtonTooltip
          type="edit"
          onClick={toggle}
          btnId="edit-showroom-contact"
          btnName="Edit Contact Information"
        />
      </div>

      {/* ✅ Modal */}
      <Modal isOpen={modal} toggle={toggle} centered size="lg">
        <ModalHeader toggle={toggle}>Edit Showroom Contact</ModalHeader>
        <ModalBody>
          <Form
            onSubmit={e => {
              e.preventDefault()
              validation.handleSubmit()
            }}
          >
            <Row>
              <Col md={6}>
                <Label>Mobile *</Label>
                <Input
                  name="mobile"
                  value={validation.values.mobile}
                  onChange={validation.handleChange}
                  invalid={!!validation.errors.mobile}
                />
                <FormFeedback>{validation.errors.mobile}</FormFeedback>
              </Col>

              <Col md={6}>
                <Label>Alt Mobile</Label>
                <Input
                  name="alt_mobile"
                  value={validation.values.alt_mobile}
                  onChange={validation.handleChange}
                  invalid={!!validation.errors.alt_mobile}
                />
                <FormFeedback>{validation.errors.alt_mobile}</FormFeedback>
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md={6}>
                <Label>Email</Label>
                <Input
                  type="email"
                  name="email"
                  value={validation.values.email}
                  onChange={validation.handleChange}
                  invalid={!!validation.errors.email}
                />
                <FormFeedback>{validation.errors.email}</FormFeedback>
              </Col>

              <Col md={6}>
                <Label>Website</Label>
                <Input
                  name="website"
                  value={validation.values.website}
                  onChange={validation.handleChange}
                  invalid={!!validation.errors.website}
                />
                <FormFeedback>{validation.errors.website}</FormFeedback>
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md={12}>
                <Label>Stock URL</Label>
                <Input
                  name="stock_url"
                  value={validation.values.stock_url}
                  onChange={validation.handleChange}
                  invalid={!!validation.errors.stock_url}
                />
                <FormFeedback>{validation.errors.stock_url}</FormFeedback>
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md={12}>
                <Label>Google Map</Label>
                <Input
                  name="google_map"
                  type="textarea"
                  rows={2}
                  value={validation.values.google_map}
                  onChange={validation.handleChange}
                  invalid={!!validation.errors.google_map}
                />
                <FormFeedback>{validation.errors.google_map}</FormFeedback>
              </Col>
            </Row>

            <div className="text-end mt-4">
              <Button
                type="submit"
                color="success"
                disabled={showroomEditLoading}
              >
                Save Changes
                {showroomEditLoading && "..."}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </>
  )
}
